import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { workoutPlans } from '../data/workoutPlans';
import UserSwitcher from '../components/UserSwitcher';
import NotificationSetup from '../components/NotificationSetup';
import { SkeletonCard } from '../components/SkeletonLoader';
import MiniChart from '../components/MiniChart';
import WelcomeTour from '../components/WelcomeTour';
import {
  getUserWorkoutPlans,
  getUserLastPlanUpdate,
  getUserLastPlanSummary,
  getUserMeasurements,
  getUserProfile,
  saveWorkoutHistory,
  saveDetailedWorkoutHistory,
  getUserWorkoutHistory,
  getUserMeasurementHistory,
  getCurrentUser
} from '../utils/userDataManager';
import { getUserPreferences } from '../utils/supabaseDataManager';
import { getNutritionForDate } from '../utils/nutritionManager';
import { scheduleWorkoutNotifications, clearWorkoutNotifications } from '../utils/notificationScheduler';
import './Home.css';

function Home() {
  const navigate = useNavigate();
  const [currentDay, setCurrentDay] = useState('');
  const [todayWorkout, setTodayWorkout] = useState(null);
  const [lastPlanUpdate, setLastPlanUpdate] = useState(null);
  const [planSummary, setPlanSummary] = useState(null);
  const [showWeeklyReviewPrompt, setShowWeeklyReviewPrompt] = useState(false);
  const [weekNumber, setWeekNumber] = useState(1);
  const [todayWorkoutCompleted, setTodayWorkoutCompleted] = useState(false);
  const [userStats, setUserStats] = useState({
    weight: '175 lbs',
    weightLost: '-60 lbs',
    bodyFat: '20%',
    pullups: '3'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [measurementTrends, setMeasurementTrends] = useState(null);
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);
  const [nutritionProgress, setNutritionProgress] = useState({ calories: 0, protein: 0 });

  useEffect(() => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = days[new Date().getDay()];
    setCurrentDay(today);

    // Check if user should see welcome tour
    const checkTourPreference = async () => {
      const username = getCurrentUser();
      if (username) {
        // Check database preference first
        const prefs = await getUserPreferences();
        if (prefs.hideWelcomeTour) return;

        // Fallback to localStorage for backwards compatibility
        const tourCompleted = localStorage.getItem(`${username}_tourCompleted`);
        if (!tourCompleted) {
          setShowWelcomeTour(true);
        }
      }
    };

    checkTourPreference();

    const loadData = async () => {
      setIsLoading(true);

      // Check if today's workout was already completed or skipped
      const workoutHistory = await getUserWorkoutHistory();
      if (workoutHistory) {
        const todayDate = new Date().toISOString().split('T')[0];
        const todayWorkoutInHistory = workoutHistory.find(w => {
          const workoutDate = w.date ? new Date(w.date).toISOString().split('T')[0] : '';
          return workoutDate === todayDate && w.day === today;
        });
        if (todayWorkoutInHistory) {
          setTodayWorkoutCompleted(true);
        }
      }

      // Check if there's a custom plan from user data, otherwise use default
      const customPlans = await getUserWorkoutPlans();
      if (customPlans) {
        setTodayWorkout(customPlans[today] || workoutPlans[today]);
      } else {
        setTodayWorkout(workoutPlans[today]);
      }

      // Load last plan update info
      const lastUpdate = await getUserLastPlanUpdate();
      const summary = await getUserLastPlanSummary();
      if (lastUpdate) {
        const updateDate = new Date(lastUpdate);
        setLastPlanUpdate(updateDate);

        // Check if it's been a week since last update (7 days)
        const daysSinceUpdate = Math.floor((new Date() - updateDate) / (1000 * 60 * 60 * 24));
        const currentWeek = Math.floor(daysSinceUpdate / 7) + 1;
        setWeekNumber(currentWeek);

        // Show weekly review prompt if it's Sunday and been 7+ days
        if (today === 'sunday' && daysSinceUpdate >= 7) {
          setShowWeeklyReviewPrompt(true);
        }
      }
      if (summary) setPlanSummary(summary);

      // Load user measurements from current user's data
      const measurements = await getUserMeasurements();
      const profile = await getUserProfile();

      // Load measurement history for trends
      const measurementHistory = await getUserMeasurementHistory();
      if (measurementHistory && measurementHistory.length > 0) {
        const weightData = measurementHistory.map((m, i) => ({ value: parseFloat(m.weight) || 0, date: m.date })).filter(d => d.value > 0);
        const bodyFatData = measurementHistory.map((m, i) => ({ value: parseFloat(m.bodyFat) || 0, date: m.date })).filter(d => d.value > 0);
        const pullupsData = measurementHistory.map((m, i) => ({ value: parseInt(m.pullups) || 0, date: m.date })).filter(d => d.value > 0);

        setMeasurementTrends({
          weight: weightData.length > 0 ? weightData : null,
          bodyFat: bodyFatData.length > 0 ? bodyFatData : null,
          pullups: pullupsData.length > 0 ? pullupsData : null
        });
      }

      if (measurements && Object.keys(measurements).length > 0) {
        const stats = {
          weight: measurements.weight ? `${measurements.weight} lbs` : 'N/A',
          bodyFat: measurements.bodyFat ? `${measurements.bodyFat}%` : 'N/A',
          pullups: measurements.pullups || '0',
          pushups: measurements.pushups || '0'
        };

        // Calculate weight lost if we have starting weight from profile
        if (profile && profile.weight && measurements.weight) {
          const lost = profile.weight - measurements.weight;
          stats.weightLost = lost > 0 ? `-${lost} lbs` : `+${Math.abs(lost)} lbs`;
        } else {
          stats.weightLost = 'N/A';
        }

        setUserStats(stats);
      } else if (profile) {
        // If no measurements but we have profile data, use that
        setUserStats({
          weight: profile.weight ? `${profile.weight} lbs` : 'N/A',
          weightLost: 'N/A',
          bodyFat: profile.bodyFat ? `${profile.bodyFat}%` : 'N/A',
          pullups: profile.pullups || '0',
          pushups: profile.pushups || '0'
        });
      }

      // Load today's nutrition progress
      const todayDate = new Date().toISOString().split('T')[0];
      const nutritionEntries = await getNutritionForDate(todayDate);
      if (nutritionEntries && nutritionEntries.length > 0) {
        const totalCalories = nutritionEntries.reduce((sum, entry) => sum + (entry.calories || 0), 0);
        const totalProtein = nutritionEntries.reduce((sum, entry) => sum + (entry.protein || 0), 0);
        setNutritionProgress({ calories: totalCalories, protein: totalProtein });
      }

      setIsLoading(false);
    };
    loadData();

    // Schedule workout notifications
    scheduleWorkoutNotifications();

    // Cleanup on unmount
    return () => {
      clearWorkoutNotifications();
    };
  }, []);

  const getDayName = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  };

  const handleSkipWorkout = async () => {
    // First alert with the motivation message
    alert("You're being a little bitch. I got something for your ass tomorrow! 💪");

    // Then confirm they really want to skip
    const confirmSkip = window.confirm("Are you sure you want to skip today's workout? This will be logged in your history.");

    if (confirmSkip) {
      // Log the skipped workout
      const skippedWorkoutData = {
        day: currentDay,
        date: new Date().toISOString(),
        workoutName: todayWorkout.name,
        completedExercises: 0,
        totalExercises: todayWorkout.exercises.length,
        skipped: true
      };

      const detailedSkippedData = {
        day: currentDay,
        date: new Date().toISOString(),
        workoutName: todayWorkout.name,
        skipped: true,
        exercises: []
      };

      try {
        // Save to history
        await saveWorkoutHistory(skippedWorkoutData);
        await saveDetailedWorkoutHistory(detailedSkippedData);

        // Set state to hide workout card immediately
        setTodayWorkoutCompleted(true);

        alert("Workout skipped and logged. Tomorrow's going to be tough! 😤");
      } catch (error) {
        console.error('Error logging skipped workout:', error);
        alert('Failed to log skipped workout. Please try again.');
      }
    }
  };

  return (
    <div className="container">
      <div className="home">
        {/* Header */}
        <div className="header">
          <div className="header-content">
            <div>
              <h1 className="header-title">Functional Strength</h1>
              <p className="header-subtitle">Build the body you deserve</p>
            </div>
            <UserSwitcher />
          </div>
        </div>

        {/* Today's Date */}
        <div className="date-container">
          <h2 className="day-name">{getDayName()}</h2>
          <p className="date">{new Date().toLocaleDateString()}</p>
        </div>

        {/* Notification Setup */}
        <NotificationSetup />

        {/* Weekly Review Prompt */}
        {showWeeklyReviewPrompt && (
          <div className="weekly-review-prompt">
            <div className="prompt-header">
              <span className="prompt-icon">🎯</span>
              <div>
                <h3 className="prompt-title">Week {weekNumber} Complete!</h3>
                <p className="prompt-subtitle">Time to review your progress and plan Week {weekNumber + 1}</p>
              </div>
            </div>
            <p className="prompt-message">
              Update your measurements and let the AI analyze your progress to create next week's optimized workout and nutrition plan.
            </p>
            <button
              className="review-now-button"
              onClick={() => navigate('/weekly-review')}
            >
              📊 Start Weekly Review
            </button>
            <button
              className="dismiss-prompt-button"
              onClick={() => setShowWeeklyReviewPrompt(false)}
            >
              Remind Me Later
            </button>
          </div>
        )}

        {/* Today's Workout Card */}
        {isLoading ? (
          <SkeletonCard />
        ) : !todayWorkoutCompleted && todayWorkout && (
        <div className="workout-card">
          <div className="workout-header">
            <h3 className="workout-name">{todayWorkout.name}</h3>
            <div className="workout-meta">
              <span className="workout-time">⏰ {todayWorkout.time}</span>
              <span className="workout-duration">⏱️ {todayWorkout.duration}</span>
            </div>
            <p className="workout-location">📍 {todayWorkout.location}</p>
            <p className="workout-focus">🎯 Focus: {todayWorkout.focus}</p>

            {todayWorkout.coachingInsight && (
              <div className="coaching-insight-home">
                <div className="insight-label-home">💬 Coach's Strategy for Today:</div>
                <p className="insight-text-home">{todayWorkout.coachingInsight}</p>
              </div>
            )}
          </div>

          {/* Nutrition Goals */}
          {(todayWorkout.calorieGoal || todayWorkout.proteinGoal) && (
            <div
              className="nutrition-goals clickable"
              onClick={() => navigate('/nutrition')}
              style={{ cursor: 'pointer' }}
            >
              <h4 className="nutrition-title">🍽️ Today's Nutrition Goals (Click to Track)</h4>
              <div className="nutrition-progress-container">
                {todayWorkout.calorieGoal && (
                  <div className="nutrition-progress-item">
                    <div className="nutrition-progress-header">
                      <span className="nutrition-progress-label">Calories</span>
                      <span className={`nutrition-progress-value ${nutritionProgress.calories > parseInt(todayWorkout.calorieGoal) ? 'over-goal' : ''}`}>
                        {nutritionProgress.calories} / {todayWorkout.calorieGoal}
                      </span>
                    </div>
                    <div className="nutrition-progress-bar-container">
                      <div
                        className={`nutrition-progress-bar ${nutritionProgress.calories > parseInt(todayWorkout.calorieGoal) ? 'over-goal' : ''}`}
                        style={{
                          width: `${Math.min((nutritionProgress.calories / parseInt(todayWorkout.calorieGoal)) * 100, 100)}%`
                        }}
                      />
                    </div>
                  </div>
                )}
                {todayWorkout.proteinGoal && (
                  <div className="nutrition-progress-item">
                    <div className="nutrition-progress-header">
                      <span className="nutrition-progress-label">Protein</span>
                      <span className={`nutrition-progress-value ${nutritionProgress.protein > parseInt(todayWorkout.proteinGoal) ? 'over-goal' : ''}`}>
                        {nutritionProgress.protein}g / {todayWorkout.proteinGoal}g
                      </span>
                    </div>
                    <div className="nutrition-progress-bar-container">
                      <div
                        className={`nutrition-progress-bar ${nutritionProgress.protein > parseInt(todayWorkout.proteinGoal) ? 'over-goal' : ''}`}
                        style={{
                          width: `${Math.min((nutritionProgress.protein / parseInt(todayWorkout.proteinGoal)) * 100, 100)}%`
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
              {todayWorkout.nutritionNotes && (
                <p className="nutrition-notes">💡 {todayWorkout.nutritionNotes}</p>
              )}
            </div>
          )}

          {/* Exercise Count */}
          <div className="exercise-count">
            <p className="exercise-count-text">
              {todayWorkout.exercises.length} Exercises
            </p>
          </div>

          {/* Start Workout Button */}
          <button
            className="start-button"
            onClick={() => navigate('/workout', { state: { workout: todayWorkout, day: currentDay } })}
          >
            START WORKOUT
          </button>

          {/* Workout Options */}
          <div className="workout-options-home">
            {todayWorkout.location && (todayWorkout.location.toLowerCase().includes('gym') || todayWorkout.location.toLowerCase().includes('planet')) && todayWorkout.homeAlternative && (
              <button
                className="home-switch-btn"
                onClick={() => {
                  const homeWorkout = {
                    ...todayWorkout,
                    name: todayWorkout.homeAlternative.name,
                    location: 'Home',
                    exercises: todayWorkout.homeAlternative.exercises
                  };
                  navigate('/workout', { state: { workout: homeWorkout, day: currentDay } });
                }}
              >
                🏠 Start Home Workout Instead
              </button>
            )}
            <button
              className="skip-day-btn"
              onClick={handleSkipWorkout}
            >
              ⏭️ Skip Today
            </button>
          </div>
        </div>
        )}

        {/* AI Weekly Planner Card */}
        <div className="ai-planner-card">
          <div className="ai-planner-header">
            <span className="ai-icon">🧠</span>
            <div className="ai-planner-text">
              <h3 className="ai-planner-title">AI Weekly Planner {lastPlanUpdate && `(Week ${weekNumber})`}</h3>
              {lastPlanUpdate ? (
                <p className="ai-planner-status">
                  Last updated: {lastPlanUpdate.toLocaleDateString()}
                </p>
              ) : (
                <p className="ai-planner-status">Using default program</p>
              )}
            </div>
          </div>
          {planSummary && (
            <p className="ai-plan-summary">{planSummary}</p>
          )}
          <button
            className="open-planner-button"
            onClick={() => navigate('/weekly-planner')}
          >
            {lastPlanUpdate ? '🔄 Generate New Plan' : '🚀 Let Claude Design Your Week'}
          </button>
          {lastPlanUpdate && (
            <button
              className="weekly-review-link"
              onClick={() => navigate('/weekly-review')}
            >
              📊 Weekly Review & Update Measurements
            </button>
          )}
        </div>

        {/* Progress Trends */}
        {measurementTrends && (measurementTrends.weight || measurementTrends.bodyFat || measurementTrends.pullups) && (
          <div className="stats-container">
            <h3 className="section-title">📊 Progress Trends</h3>
            <div className="trends-grid">
              {measurementTrends.weight && (
                <MiniChart
                  data={measurementTrends.weight}
                  label="Weight (lbs)"
                  color="#FF6B35"
                />
              )}
              {measurementTrends.bodyFat && (
                <MiniChart
                  data={measurementTrends.bodyFat}
                  label="Body Fat %"
                  color="#f39c12"
                />
              )}
              {measurementTrends.pullups && (
                <MiniChart
                  data={measurementTrends.pullups}
                  label="Pull-ups"
                  color="#4ECDC4"
                  showTrend={true}
                />
              )}
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="stats-container">
          <h3 className="section-title">Your Progress</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <p className="stat-value">{userStats.weight}</p>
              <p className="stat-label">Current Weight</p>
            </div>
            <div className="stat-card">
              <p className="stat-value">{userStats.weightLost}</p>
              <p className="stat-label">Lost</p>
            </div>
            <div className="stat-card">
              <p className="stat-value">{userStats.bodyFat}</p>
              <p className="stat-label">Body Fat</p>
            </div>
            <div className="stat-card">
              <p className="stat-value">{userStats.pullups}</p>
              <p className="stat-label">Pull-ups</p>
            </div>
          </div>
          <button
            className="view-progress-button"
            onClick={() => navigate('/progress')}
          >
            View Detailed Progress →
          </button>
        </div>

        {/* Quick Access */}
        <div className="quick-access">
          <h3 className="section-title">Quick Access</h3>
          <button className="quick-button" onClick={() => navigate('/nutrition')}>
            🍽️ Nutrition Tracker
          </button>
          <button className="quick-button" onClick={() => navigate('/schedule')}>
            📅 Weekly Schedule
          </button>
          <button className="quick-button" onClick={() => navigate('/exercises')}>
            💪 Exercise Library
          </button>
          <button className="quick-button" onClick={() => navigate('/settings')}>
            ⚙️ Settings
          </button>
        </div>

        {/* Motivation Quote */}
        {userStats.weightLost !== 'N/A' && userStats.weightLost.includes('-') && (
          <div className="motivation-card">
            <p className="motivation-text">
              "You've already lost {userStats.weightLost.replace('-', '').trim()}. Now let's build the strength to match that discipline."
            </p>
          </div>
        )}
      </div>

      {/* Welcome Tour */}
      {showWelcomeTour && (
        <WelcomeTour onComplete={() => setShowWelcomeTour(false)} />
      )}
    </div>
  );
}

export default Home;
